import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { firestoreService } from '@/data/services/firestoreService';
import { UserMediaRemapper } from '@/domain/remappers/userMedia/userMediaRemapper';
import { shouldFilterOutBlockedOrHiddenMediaUseCase } from '@/domain/usecases/userMedia/shouldFilterOutBlockedOrHiddenMediaUseCase';

/**
 * Mirrors Flutter's MediasRepository.getMediaByPath
 * (lib/data/repository/medias_repository.dart:196). Used to resolve a media
 * referenced by `relativePath` (e.g. `UserMediaEntity.contentLink`).
 *
 * Applies the same three blocked/hidden guards as Flutter's
 * `content_data_source.dart#getMediaByPath`: media is dropped (returns null)
 * if it is blocked for non-owners, if its creator has blocked the viewer, or
 * if its id appears in the viewer's hiddenContent.
 */
export class GetUserMediaByPathUseCase {
    async execute(path: string, currentUser: UserEntity | null): Promise<UserMediaEntity | null> {
        const json = await firestoreService.getUserMediaByPath(path);

        if (!json) {
            return null;
        }

        const media = UserMediaRemapper.toEntity(json) as UserMediaEntity;

        if (shouldFilterOutBlockedOrHiddenMediaUseCase.execute(media, currentUser)) {
            return null;
        }

        return media;
    }
}

export const getUserMediaByPathUseCase = new GetUserMediaByPathUseCase();
